import ExcelJS from 'exceljs';
import { Result } from './models';

/**
 * Build an Excel Workbook from the extracted data of RPT files.
 */
export async function exportDataToExcel(results: Result[]): Promise<ExcelJS.Buffer> {
  if (results.length === 0) {
    throw new Error('Results are empty');
  }
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('results');
  const nuclideNames = buildRnList(results);
  const { metadataLen, headers } = buildHeaders(nuclideNames, 3);
  worksheet.columns = headers;
  const subHeaders = buildSubHeaders(metadataLen, nuclideNames.length);
  worksheet.addRow(subHeaders);

  for (const res of results) {
    const rows = buildRows(res, nuclideNames);
    worksheet.addRow(rows);
  }

  return await workbook.xlsx.writeBuffer();
}

/**
 * Search for all RN names in dataset and return a sorted array of names.
 * @param results Extracted RPT files data
 * @returns Sorted array of names
 */
function buildRnList(results: Result[]): string[] {
  const nuclideNames = results.reduce((set, result) => {
    result.nuclideData.forEach((nuclide) => set.add(nuclide.nuclideName));
    return set;
  }, new Set<string>());
  const nuclideNamesArray = Array.from(nuclideNames);
  function extractNumber(str: string): number {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }
  nuclideNamesArray.sort((a, b) => extractNumber(a) - extractNumber(b));
  return nuclideNamesArray;
}

/**
 * Build the headers of the worksheet
 * @param rnList
 * @returns
 */
function buildHeaders(rnList: string[], dataCols: number): { metadataLen: number; headers: ExcelJS.Column[] } {
  const headers: ExcelJS.Column[] = [];
  // METADATA
  headers.push({
    header: 'Nom de fichier',
    key: 'fileName',
    width: 20
  } as ExcelJS.Column);
  headers.push({
    header: 'Référence échantillon',
    key: 'sampleName',
    width: 20
  } as ExcelJS.Column);
  headers.push({
    header: 'Date de mesure',
    key: 'measureDate',
    width: 16
  } as ExcelJS.Column);
  headers.push({
    header: 'Temps actif',
    key: 'activeTime',
    width: 13
  } as ExcelJS.Column);
  headers.push({
    header: 'Temps mort (%)',
    key: 'deadTime',
    width: 15
  } as ExcelJS.Column);
  headers.push({
    header: 'Taille',
    key: 'size',
    width: 10
  } as ExcelJS.Column);
  headers.push({
    header: 'Unité',
    key: 'sizeUnit',
    width: 10
  } as ExcelJS.Column);
  const metadataLen = headers.length;

  // DATA
  for (const name of rnList) {
    headers.push({
      header: name,
      key: name,
      width: 10
    } as ExcelJS.Column);
    for (let i = 0; i < dataCols - 1; i++) {
      headers.push({
        header: '',
        key: `${name}_empty_${i}`,
        width: 10
      } as ExcelJS.Column);
    }
  }
  return { metadataLen, headers };
}

/**
 * Build headers of data values on second line
 * @param metadataLen
 * @param rnCount
 * @returns
 */
function buildSubHeaders(metadataLen: number, rnCount: number): string[] {
  const rowValues: string[] = [];

  for (let i = 0; i < metadataLen; i++) {
    rowValues.push('');
  }
  for (let i = 0; i < rnCount; i++) {
    rowValues.push('Limite de détection');
    rowValues.push('Meilleur activité');
    rowValues.push('Incertitude');
  }

  return rowValues;
}

/**
 * Insert values into the Worksheet
 * @param results Extracted RPT single file data
 * @returns
 */
function buildRows(result: Result, nuclideNames: string[]): string[] {
  const rowValues: string[] = [];
  // METADATA
  rowValues.push(result.fileName || 'N/A');
  rowValues.push(result.header.sampleName || 'N/A');
  rowValues.push(formatDate(result.header.measureDate) || 'N/A');
  rowValues.push(result.header.activeTime.toString() || 'N/A');
  rowValues.push(result.header.deadTime.toString() || 'N/A');
  rowValues.push(result.header.size.toString() || 'N/A');
  rowValues.push(result.header.sizeUnit || 'N/A');

  // DATA
  for (const nuclideName of nuclideNames) {
    const data = result.nuclideData.find((element) => element.nuclideName === nuclideName);
    if (data) {
      rowValues.push(
        ...[data.nuclideDL.toString(), data.bestActivity.toString(), data.estimationUncertainty.toString()]
      );
    } else {
      rowValues.push(...['', '', '']);
    }
  }
  return rowValues;
}

/**
 * Format date before insertion in Worksheet
 * @param date
 * @returns
 */
function formatDate(date: Date): string {
  if (!date) {
    return 'N/A';
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Les mois commencent à 0
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}
